import React, { useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  WSElement,
  WSFlexBox,
  WSText,
  WSButton,
  WSLoader
} from "@wingspanhq/fe-component-library";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { LayoutFullscreen } from "../../../shared/components/LayoutFullscreen";
import { AuthorizeConsent } from "../components/AuthorizeConsent";
import {
  useQueryOAuthAuthorize,
  useMutationOAuthAuthorize
} from "../hooks/useOAuthAuthorize";
import { IOAuthAuthorizeParams, IOAuthAuthorizeSubmit } from "../types";
import styles from "./RouteAuthorize.module.scss";

export const RouteAuthorize: React.FC = () => {
  useBrowserPageTitle("Authorize Application");
  const location = useLocation();
  const history = useHistory();

  // Parse OAuth parameters from URL
  const oauthParams = useMemo<IOAuthAuthorizeParams | null>(() => {
    const params = new URLSearchParams(location.search);
    const response_type = params.get("response_type");
    const client_id = params.get("client_id");
    const redirect_uri = params.get("redirect_uri");

    if (!response_type || !client_id || !redirect_uri) {
      return null;
    }

    return {
      response_type,
      client_id,
      redirect_uri,
      scope: params.get("scope") || undefined,
      state: params.get("state") || undefined,
      code_challenge: params.get("code_challenge") || undefined,
      code_challenge_method: params.get("code_challenge_method") || undefined
    };
  }, [location.search]);

  // Query for authorization details
  const {
    data: authorizeData,
    isLoading,
    error,
    refetch
  } = useQueryOAuthAuthorize(oauthParams || ({} as IOAuthAuthorizeParams));

  // Mutation for submitting authorization
  const [submitAuthorization, submitMeta] = useMutationOAuthAuthorize();

  // Handle missing parameters
  if (!oauthParams) {
    return (
      <LayoutFullscreen whiteBackground>
        <WSElement className={styles.errorContainer}>
          <WSText.Heading2 mb="M" color="red500">
            Invalid Request
          </WSText.Heading2>
          <WSText.Paragraph mb="L">
            Required OAuth parameters are missing. Please check the
            authorization URL and try again.
          </WSText.Paragraph>
          <WSButton.Secondary onClick={() => history.push("/member/dashboard")}>
            Go to Dashboard
          </WSButton.Secondary>
        </WSElement>
      </LayoutFullscreen>
    );
  }

  // Handle loading state
  if (isLoading) {
    return (
      <LayoutFullscreen whiteBackground>
        <WSFlexBox.Center>
          <WSFlexBox direction="column" gap="M">
            <WSLoader.Spinner size="M" />
            <WSText.Paragraph>
              Loading authorization details...
            </WSText.Paragraph>
          </WSFlexBox>
        </WSFlexBox.Center>
      </LayoutFullscreen>
    );
  }

  // Handle error state
  if (error || !authorizeData) {
    return (
      <LayoutFullscreen whiteBackground>
        <WSElement className={styles.errorContainer}>
          <WSText.Heading2 mb="M" className={styles.errorTitle}>
            Authorization Error
          </WSText.Heading2>
          <WSErrorMessage error={error} mb="L" contextKey="Other" />
          <WSFlexBox.Center gap="M">
            <WSButton.Secondary onClick={() => refetch()}>
              Try Again
            </WSButton.Secondary>
            <WSButton.Secondary
              onClick={() => history.push("/member/dashboard")}
            >
              Go to Dashboard
            </WSButton.Secondary>
          </WSFlexBox.Center>
        </WSElement>
      </LayoutFullscreen>
    );
  }

  const handleApprove = () => {
    if (!oauthParams) return;

    const submitParams: IOAuthAuthorizeSubmit = {
      client_id: oauthParams.client_id,
      redirect_uri: oauthParams.redirect_uri,
      approved: true,
      scope: oauthParams.scope,
      state: oauthParams.state,
      code_challenge: oauthParams.code_challenge,
      code_challenge_method: oauthParams.code_challenge_method
    };

    submitAuthorization(submitParams);
  };

  const handleDeny = () => {
    if (!oauthParams) return;

    const submitParams: IOAuthAuthorizeSubmit = {
      client_id: oauthParams.client_id,
      redirect_uri: oauthParams.redirect_uri,
      approved: false,
      scope: oauthParams.scope,
      state: oauthParams.state,
      code_challenge: oauthParams.code_challenge,
      code_challenge_method: oauthParams.code_challenge_method
    };

    submitAuthorization(submitParams);
  };

  return (
    <LayoutFullscreen whiteBackground>
      {submitMeta.error && (
        <WSElement mb="M" className={styles.errorMessageWrapper}>
          <WSErrorMessage error={submitMeta.error} contextKey="Other" />
        </WSElement>
      )}
      <AuthorizeConsent
        authorizeData={authorizeData}
        onApprove={handleApprove}
        onDeny={handleDeny}
        isLoading={submitMeta.isLoading}
      />
    </LayoutFullscreen>
  );
};
